/**
 * Pure builders for the Localization panel's format-option dropdowns. Each
 * dedups the browser default against the configured format enum and returns a
 * fresh `SelectOption[]`. No `vue`/`ref`/IO — the shell resolves the browser
 * defaults (locale / week info) and passes them in as plain values.
 */

export interface SelectOption {
  title: string
  value: string
}

/** The weekday name for a 0-based first-day-of-week index (0 = Sunday). */
export function firstDayOfWeekWeekday(
  firstDayOfWeek: number
): string | undefined {
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ]
  return weekdays[firstDayOfWeek]
}

/** Date-format options, with the browser locale pinned first. */
export function dateFormatOptions(
  browserDefault: string,
  formats: Readonly<Record<string, string>>
): SelectOption[] {
  const result = new Map<string, SelectOption>()
  result.set(browserDefault, {
    title: 'Browser Default',
    value: browserDefault
  })
  for (const [key, value] of Object.entries(formats)) {
    if (result.has(value)) continue
    result.set(value, { title: `${key} (${value})`, value })
  }
  return Array.from(result.values())
}

/** Week-number options, with the browser weekend pinned first when known. */
export function weekNumberFormatOptions(
  browserDefault: string | undefined,
  formats: Readonly<Record<string, readonly number[]>>
): SelectOption[] {
  const result = new Map<string, SelectOption>()
  if (browserDefault) {
    result.set(browserDefault, {
      title: 'Browser Default',
      value: browserDefault
    })
  }
  for (const [key, value] of Object.entries(formats)) {
    const optionValue = value.toString()
    if (result.has(optionValue)) continue
    result.set(optionValue, { title: key, value: optionValue })
  }
  return Array.from(result.values())
}

/** First-day-of-week options, with the browser first-day pinned first. */
export function firstDayOfWeekOptions(
  browserDefault: string | undefined,
  formats: Readonly<Record<string, string>>
): SelectOption[] {
  const result = new Map<string, SelectOption>()
  if (browserDefault) {
    result.set(browserDefault, {
      title: `Browser Default (${firstDayOfWeekWeekday(Number(browserDefault))})`,
      value: browserDefault
    })
  }
  for (const [key, value] of Object.entries(formats)) {
    if (result.has(value)) continue
    result.set(value, { title: key, value: value.toString() })
  }
  return Array.from(result.values())
}

/** Number-format options, formatting a sample number for each locale. */
export function numberFormatOptions(
  browserDefault: string | undefined,
  formats: Readonly<Record<string, string>>
): SelectOption[] {
  const result = new Map<string, SelectOption>()
  if (browserDefault) {
    result.set(browserDefault, {
      title: 'Browser Default',
      value: browserDefault
    })
  }
  for (const [, value] of Object.entries(formats)) {
    if (result.has(value)) continue
    result.set(value, {
      title: `${new Intl.NumberFormat(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(10000)} (${value})`,
      value
    })
  }
  return Array.from(result.values())
}

/** The title of the option matching `value`, or an empty string. */
export function findOptionTitle(
  options: readonly SelectOption[],
  value: string | undefined
): string {
  return options.find((o) => o.value === value)?.title ?? ''
}
